import { useQuery } from '@apollo/client/react';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import LoginIcon from '@mui/icons-material/Login';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { useNavigate } from '@tanstack/react-location';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { useState } from 'react';
import type Booking from '../../../../../packages/entities/Booking';
import BookingMessageGenerator from '../../booking/components/BookingMessageGenerator';
import GuestBookView from '../../booking/components/operation/GuestBookView';
import { InvoiceViewDialog } from '../../booking/components/operation/InvoiceView';
import ReviewDialog from '../../booking/components/ReviewDialog';
import ErrorBox from '../../core/components/ErrorBox';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import assert from '../../core/util/assert';
import { Booking as DomainBooking } from '../../domain/booking';
import SpinnerIcon from '../../main/component/icon/SpinnerIcon';
import { checklistQuery } from '../../main/queries';
import useBookingActions from '../hooks/useBookingActions';
import TaskWidget from './TaskWidget';
import WidgetBox from './WidgetBox';

const InfoChip = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.875rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'rgba(255, 255, 255, 0.85)',
    '& svg': {
        fontSize: '1rem',
        color: '#6366f1',
    },
});

const BookingSetupActionWidget = ({ booking }: { booking: Booking }) => {
    const navigate = useNavigate();
    const propertyId = usePropertyId();

    const daysUntilArrival = differenceInCalendarDays(
        booking.checkin,
        new Date().toISOString().slice(0, 10)
    );

    const isUrgent = daysUntilArrival <= 10;

    return (
        <WidgetBox>
            <InfoChip sx={{ color: isUrgent ? 'error.main' : undefined }}>
                <CalendarTodayIcon
                    sx={{ color: isUrgent ? 'error.main' : undefined }}
                />
                {daysUntilArrival} days
            </InfoChip>
            <Button
                variant="contained"
                color={isUrgent ? 'error' : 'warning'}
                startIcon={<SettingsIcon />}
                onClick={() => {
                    navigate({
                        to: propertyId
                            ? `/${propertyId}/booking/${booking.id}`
                            : `/booking/${booking.id}`,
                    });
                }}
                sx={{
                    borderRadius: 0,
                    border: '2px solid',
                    borderColor: isUrgent ? 'error.main' : 'warning.main',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    px: 3,
                    py: 1,
                }}
            >
                Setup {booking.guestName}
            </Button>
        </WidgetBox>
    );
};

const BookingReviewActionWidget = ({
    booking,
    onSuccess,
}: {
    booking: Booking;
    onSuccess?: () => void;
}) => {
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const { loading, error, data } = useQuery<{ booking: DomainBooking }>(
        checklistQuery,
        {
            variables: { id: booking.id },
            skip: !reviewDialogOpen,
        }
    );

    if (loading && reviewDialogOpen) {
        return (
            <WidgetBox>
                <SpinnerIcon />
            </WidgetBox>
        );
    }

    return (
        <WidgetBox>
            <Button
                variant="contained"
                color="primary"
                startIcon={<RateReviewIcon />}
                onClick={() => {
                    setReviewDialogOpen(true);
                }}
                sx={{
                    borderRadius: 0,
                    border: '2px solid #6366f1',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    px: 3,
                    py: 1,
                }}
            >
                Review {booking.guestName}
            </Button>
            {reviewDialogOpen && data && !error && (
                <>
                    {assert(data)}
                    <ReviewDialog
                        booking={data.booking}
                        onClose={() => {
                            setReviewDialogOpen(false);
                        }}
                        onSuccess={onSuccess}
                    />
                </>
            )}
        </WidgetBox>
    );
};

const BookingInvoiceActionWidget = ({
    booking,
    onSuccess,
}: {
    booking: Booking;
    onSuccess?: () => void;
}) => {
    const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);

    if (!booking.id) {
        return null;
    }

    return (
        <WidgetBox>
            <Button
                variant="contained"
                color="error"
                startIcon={<ReceiptIcon />}
                onClick={() => {
                    setCreateInvoiceModalOpen(true);
                }}
                sx={{
                    borderRadius: 0,
                    border: '2px solid',
                    borderColor: 'error.main',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    px: 3,
                    py: 1,
                }}
            >
                Invoice {booking.guestName}
            </Button>
            {createInvoiceModalOpen && (
                <InvoiceViewDialog
                    bookingId={booking.id}
                    onFocusLost={() => {
                        setCreateInvoiceModalOpen(false);
                    }}
                    onSuccess={onSuccess}
                />
            )}
        </WidgetBox>
    );
};

const GuestDetailRequestActionWidget = ({
    booking,
    onSuccess,
}: {
    booking: Booking;
    onSuccess?: () => void;
}) => {
    const [messageDialogOpen, setMessageDialogOpen] = useState(false);

    const daysUntilArrival = differenceInCalendarDays(
        booking.checkin,
        new Date().toISOString().slice(0, 10)
    );

    const totalGuests =
        booking.adultCount + booking.childCount + booking.infantCount;

    if (!booking.id) {
        return null;
    }

    const isUrgent = daysUntilArrival <= 10;

    return (
        <WidgetBox>
            <Box sx={{ display: 'flex', gap: 3 }}>
                <InfoChip sx={{ color: isUrgent ? 'error.main' : undefined }}>
                    <CalendarTodayIcon
                        sx={{ color: isUrgent ? 'error.main' : undefined }}
                    />
                    {daysUntilArrival} days
                </InfoChip>
                <InfoChip>
                    <GroupIcon />
                    {totalGuests} guests
                </InfoChip>
            </Box>
            <Button
                variant="contained"
                color={isUrgent ? 'error' : 'warning'}
                startIcon={<MailOutlineIcon />}
                onClick={() => {
                    setMessageDialogOpen(true);
                }}
                sx={{
                    borderRadius: 0,
                    border: '2px solid',
                    borderColor: isUrgent ? 'error.main' : 'warning.main',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    px: 3,
                    py: 1,
                }}
            >
                Request details for {booking.guestName}
            </Button>
            {messageDialogOpen && (
                <BookingMessageGenerator
                    bookingId={booking.id}
                    onClose={() => {
                        setMessageDialogOpen(false);
                    }}
                    onSuccess={onSuccess}
                />
            )}
        </WidgetBox>
    );
};

const GovernmentPortalActionWidget = ({
    booking,
    onSuccess,
}: {
    booking: Booking;
    onSuccess?: () => void;
}) => {
    const [guestBookDialogOpen, setGuestBookDialogOpen] = useState(false);

    const daysUntilArrival = differenceInCalendarDays(
        booking.checkin,
        new Date().toISOString().slice(0, 10)
    );

    if (!booking.id) {
        return null;
    }

    const isUrgent = daysUntilArrival <= 3;

    return (
        <WidgetBox>
            <InfoChip sx={{ color: isUrgent ? 'error.main' : undefined }}>
                <CalendarTodayIcon
                    sx={{ color: isUrgent ? 'error.main' : undefined }}
                />
                {daysUntilArrival} days
            </InfoChip>
            <Button
                variant="contained"
                color={isUrgent ? 'error' : 'warning'}
                startIcon={<AccountBalanceIcon />}
                onClick={() => {
                    setGuestBookDialogOpen(true);
                }}
                sx={{
                    borderRadius: 0,
                    border: '2px solid',
                    borderColor: isUrgent ? 'error.main' : 'warning.main',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    px: 3,
                    py: 1,
                }}
            >
                Gov portal {booking.guestName}
            </Button>
            {guestBookDialogOpen && (
                <GuestBookView
                    bookingId={booking.id}
                    onClose={() => {
                        setGuestBookDialogOpen(false);
                    }}
                    onSuccess={onSuccess}
                />
            )}
        </WidgetBox>
    );
};

const BookingActionsWidget = () => {
    const navigate = useNavigate();
    const propertyId = usePropertyId();
    const {
        error,
        loading,
        data: actions,
        reload,
    } = useBookingActions({
        params: [],
    });

    if (loading) {
        return null;
    }

    if (error) {
        return <ErrorBox error={error} />;
    }

    if (!actions.length) {
        return null;
    }

    return (
        <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
            {actions.map(action => {
                const { type } = action;
                switch (type) {
                    case 'checkin':
                        return (
                            <WidgetBox key={`${type}${action.booking.id}`}>
                                <Button
                                    onClick={() => {
                                        navigate({
                                            to: propertyId
                                                ? `/${propertyId}/checkin/${action.booking.id}`
                                                : `/checkin/${action.booking.id}`,
                                        });
                                    }}
                                    variant="contained"
                                    color="error"
                                    startIcon={<LoginIcon />}
                                    sx={{
                                        borderRadius: 0,
                                        border: '2px solid',
                                        borderColor: 'error.main',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 3,
                                        py: 1,
                                    }}
                                >
                                    Checkin {action.booking.guestName}
                                </Button>
                            </WidgetBox>
                        );
                    case 'setup':
                        return (
                            <BookingSetupActionWidget
                                key={`${type}${action.booking.id}`}
                                booking={action.booking}
                            />
                        );
                    case 'review':
                        return (
                            <BookingReviewActionWidget
                                key={`${type}${action.booking.id}`}
                                booking={action.booking}
                                onSuccess={reload}
                            />
                        );
                    case 'invoice':
                        return (
                            <BookingInvoiceActionWidget
                                key={`${type}${action.booking.id}`}
                                booking={action.booking}
                                onSuccess={reload}
                            />
                        );
                    case 'touristTax':
                        return (
                            <TaskWidget
                                task={action}
                                key={`${type}${action.id}`}
                                onSuccess={reload}
                            />
                        );
                    case 'guestDetailRequest':
                        return (
                            <GuestDetailRequestActionWidget
                                key={`${type}${action.booking.id}`}
                                booking={action.booking}
                                onSuccess={reload}
                            />
                        );
                    case 'governmentPortal':
                        return (
                            <GovernmentPortalActionWidget
                                key={`${type}${action.booking.id}`}
                                booking={action.booking}
                                onSuccess={reload}
                            />
                        );
                    default:
                        throw new Error(`Invalid type: ${type}`);
                }
            })}
        </Box>
    );
};

export default BookingActionsWidget;
